import React, { useState, useEffect, useCallback, useMemo, useRef, Fragment } from 'react';
import { pick } from 'lodash-es';
import { useDispatch, useSelector } from 'react-redux';
import cx from 'classnames';

import { renderRowCells, loadingRowsSkeleton } from './TableCells';
import EmptyExportsTable from './EmptyExportsTable';
import DownloadExportsButton from './DownloadExportsButton';
import UpsertExportModal from '../Modals/UpsertExportModal';
import DeleteExportModal from '../Modals/DeleteExportModal';
import ExportFailedModal from '../Modals/ExportFailedModal';
import ProcessingToastContent from '../ProcessingToastContent';
import TablePerPageDropdown from '../../TablePerPageDropdown';
import Tooltip from '../../../../../components/Tooltip';
import Button from '../../../../../components/Buttons/Button';
import Pagination from '../../../../../components/Pagination';
import SearchField from '../../../../../components/Search/SearchField';
import { SortableTable } from '../../../../../components/SortableTable';
import DiverseSelection from '../../../../../components/Checkbox/DiverseSelection';
import ErrorStateWithReload from '../../../../../components/ErrorStateWithReload';

import { toast } from '../../../../../components/Toasts';
import { useAppCable } from '../../../../../hooks';

import {
  fetchCustomExportsData,
  updateCustomExportsData,
  selectCustomExportsData,
  changeCustomExportsPerPage,
} from '../../../actions';
import {
  customExportsSettingsSelector,
  customExportsFiltersSelector,
  customExportsReportsSelector,
  selectedCustomExportsSelector,
} from '../../../selectors';

import api from '../../../api';
import { showProcessingNotify, staticColumns, SELECTION_KIND } from '../../../configs/custom-exports-config';
import { useSuggestedReportName } from '../../../hooks/useSuggestedReportName';
import { redirectToCustomReports } from '../../../utils/custom-exports-utils';
import { initParams } from '../../../configs';

const makePreferences = ({ noneDisabled }) => ([
  { value: SELECTION_KIND.all, label: 'All' },
  { value: SELECTION_KIND.none, label: 'None', disabled: noneDisabled },
]);

const ExportsTable = () => {
  const dispatch = useDispatch();

  const settings = useSelector(customExportsSettingsSelector);
  const filters = useSelector(customExportsFiltersSelector);
  const tableData = useSelector(customExportsReportsSelector);
  const selectedExports = useSelector(selectedCustomExportsSelector);

  const activeActionRef = useRef();
  const suggestedReportNameRef = useSuggestedReportName(tableData, {
    condition: filters.pagination.current_page === 0 && filters.sort_order === 'desc',
  });

  // Memoized params
  const memoFilters = useRef();
  memoFilters.current = filters;

  const [activeExport, setActiveExport] = useState();
  const [multipleDownload, setMultipleDownload] = useState({ loading: false, zipUrl: null });
  const [isUpsertModalOpen, setUpsertModalOpen] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [showExportFailed, setExportFailed] = useState(false);

  const selectedExportsIds = useMemo(() => (
    selectedExports.map(item => item.id)
  ), [selectedExports]);

  const notableData = !tableData.length;
  const noSelectedExports = !selectedExports.length;

  const resetPagination = useCallback(() => ({
    pagination: { ...memoFilters.current.pagination, current_page: 0 },
  }), []);

  const closeExportFailed = useCallback(() => {
    setExportFailed(false);
  }, []);

  const handleExportsListUpdate = useCallback((filtersParams = {}) => {
    const updatedFilters = pick(filtersParams, ['pagination', 'query', 'sort_by', 'sort_order']);
    dispatch(fetchCustomExportsData(updatedFilters));
  }, []);

  const handleSearchChange = useCallback((query) => {
    handleExportsListUpdate({ query, ...resetPagination() });
  }, []);

  const handleUpdateSelectedExports = (e) => {
    e.preventDefault();
    if (noSelectedExports) return;
    dispatch(updateCustomExportsData());
  };

  const handleResetZipUrl = useCallback(() => {
    setMultipleDownload(prevState => ({ ...prevState, zipUrl: null }));
  }, []);

  const handleMultipleDownload = useCallback(() => {
    if (noSelectedExports) return;

    setMultipleDownload(prevState => ({ ...prevState, loading: true }));
    api.customExports.downloadCustomExports({ custom_exports: selectedExports });
  }, [selectedExports]);

  const closeUpsertModal = useCallback(() => {
    setUpsertModalOpen(false);
    activeActionRef.current = undefined;
  }, []);

  const closeDeleteModal = useCallback(() => {
    setDeleteModalOpen(false);
  }, []);

  const onExportCreate = () => {
    activeActionRef.current = 'create';
    setActiveExport({ name: suggestedReportNameRef.current });
    setUpsertModalOpen(true);
  };

  const onExportEdit = useCallback((reportToEdit) => {
    if (reportToEdit.state !== 'pending') {
      activeActionRef.current = 'update';
      redirectToCustomReports(`/${reportToEdit.id}/edit`);
    }
  }, []);

  const handleSortChange = useCallback(({ sortBy, sortOrder }) => {
    handleExportsListUpdate({
      sort_order: sortOrder,
      sort_by: sortBy,
      ...resetPagination(),
    });
    dispatch(selectCustomExportsData());
  }, []);

  const handlePageChange = useCallback(({ selected }) => {
    handleExportsListUpdate({
      pagination: { ...memoFilters.current.pagination, current_page: selected },
    });
    dispatch(selectCustomExportsData());
  }, []);

  const handlePerPageChange = useCallback((value) => {
    dispatch(changeCustomExportsPerPage(value));
  }, []);

  const receivedChannelData = useCallback((data) => {
    const { type } = data;

    if (type === 'state_transition_completed') {
      handleExportsListUpdate(memoFilters.current);
      return;
    }

    if (type === 'zip_completed') {
      setMultipleDownload({ loading: false, zipUrl: data.zip_url });
    }

    if (type === 'failed') {
      setExportFailed(true);
      setActiveExport(data.custom_export.id);
    }
  }, []);

  useAppCable({ channel: 'CustomExportsChannel' }, {
    onReceived: receivedChannelData,
  });

  // Effects
  useEffect(() => {
    if (showProcessingNotify) {
      toast.success(<ProcessingToastContent />);
    }
  }, []);

  useEffect(() => {
    if (initParams.permissions.custom_exports && !tableData.length) {
      dispatch(fetchCustomExportsData());
    }
  }, []);

  const columns = useMemo(() => {
    const availableExports = tableData.filter(({ state }) => state === 'processed');
    const preferences = makePreferences({ noneDisabled: !selectedExports.length });

    const checkboxColumn = {
      dataField: 'checkbox',
      text: '',
      className: 'checkbox-cell',
      element: (
        <DiverseSelection
          menuOptions={preferences}
          selected={selectedExports}
          selectableItems={availableExports}
          itemKey="id"
          menuPortal
          menuClassName="custom-export-diverse-selection"
          onChange={reports => dispatch(selectCustomExportsData(reports))}
        />
      ),
    };
    return [checkboxColumn, ...staticColumns];
  }, [tableData, selectedExports]);

  const rows = useMemo(() => {
    const actions = {
      onExportSelect(checked, item) {
        dispatch(selectCustomExportsData(prevState => (checked
          ? [...prevState, item]
          : prevState.filter(({ id }) => id !== item.id)
        )));
      },
      onExportUpdate(item) { dispatch(updateCustomExportsData(item.id)); },
      onExportEdit,
      onExportRename(item) {
        activeActionRef.current = 'update';
        setActiveExport(item);
        setUpsertModalOpen(true);
      },
      onExportDuplicate(item) {
        activeActionRef.current = 'duplicate';
        setActiveExport(item);
        setUpsertModalOpen(true);
      },
      onExportDelete(item) {
        setActiveExport(item);
        setDeleteModalOpen(true);
      },
    };

    return tableData.map(item => renderRowCells(item, { selectedExportsIds }, actions));
  }, [tableData, selectedExportsIds]);

  // Pagination
  const total = parseInt(filters.pagination.total, 10);
  const perPage = parseInt(filters.pagination.per_page, 10);
  const currentPage = parseInt(filters.pagination.current_page, 10);
  const showPagination = total > tableData.length;

  if (!initParams.permissions.custom_exports) return <Fragment />;

  if (settings.error) return <ErrorStateWithReload />;

  return (
    <Fragment>
      <section className="custom-exports__head">
        <h4 className="custom-exports__head-heading h4">My exports</h4>
        <p className="custom-exports__head-subheading">Exports created by you</p>
      </section>
      <section className="custom-exports__table">
        <header className="custom-exports__table-header">
          <div className="custom-exports__table-header-actions">
            <Tooltip id="update-selected-tooltip" content="Updates the end-date of the time period to the most recently available date and regenerates the export.">
              <Button
                variant="secondary"
                disabled={noSelectedExports}
                onClick={handleUpdateSelectedExports}
              >
                <i className="hicon-reset" />
                Update {selectedExports.length > 0 ? `${selectedExports.length} exports` : 'All'}
              </Button>
            </Tooltip>
            <div className="custom-exports__table-header-actions-bg">
              <DownloadExportsButton
                selectedExports={selectedExports}
                zipUrl={multipleDownload.zipUrl}
                disabled={multipleDownload.loading || noSelectedExports}
                resetZipUrl={handleResetZipUrl}
                onMultipleDownload={handleMultipleDownload}
              />
            </div>
            <SearchField
              size="sm"
              placeholder="Search exports by name"
              onChange={handleSearchChange}
            />
          </div>
          <div className="custom-exports__table-header-actions">
            <Button onClick={onExportCreate}>
              Create New Export
            </Button>
            <TablePerPageDropdown
              active={perPage}
              total={total}
              disabled={total === 0}
              onChange={handlePerPageChange}
            />
          </div>
        </header>

        <SortableTable
          tableClassName={cx('table-default table-default-max-padding sticky-table-header exports-table', { 'no-exports': notableData })}
          defaultSortBy="created_at"
          columns={columns}
          rows={settings.pending ? loadingRowsSkeleton : rows}
          initialRowsLimit={perPage}
          showMoreCellColSpan={columns.length}
          emptyStateText={<EmptyExportsTable />}
          hasShowMoreCell={false}
          onSortEnd={handleSortChange}
        />
        {showPagination && (
          <Pagination
            total={total}
            perPage={perPage}
            currentPage={currentPage}
            className="mt-sm"
            onPageChange={handlePageChange}
          />
        )}
      </section>

      {isDeleteModalOpen && (
        <DeleteExportModal
          open={isDeleteModalOpen}
          report={activeExport}
          onDeleteSuccess={handleExportsListUpdate}
          onClose={closeDeleteModal}
        />
      )}

      {isUpsertModalOpen && (
        <UpsertExportModal
          open={isUpsertModalOpen}
          report={activeExport}
          action={activeActionRef.current}
          onUpdateSuccess={handleExportsListUpdate}
          onClose={closeUpsertModal}
        />
      )}

      {showExportFailed && (
        <ExportFailedModal
          open={showExportFailed}
          report={activeExport}
          onUpdateSuccess={handleExportsListUpdate}
          onClose={closeExportFailed}
        />
      )}
    </Fragment>
  );
};

export default ExportsTable;
